import {supabase} from './supabase';
import {getPrimaryMembership} from './agentordinaryWorkspace';
import {COMPANY_BRANDS} from './companyBrands';

/** Read-only, workspace-scoped application inventory. No auth tokens or bridge actions are fetched. */
export type AppConnection={
 key:string;name:string;kind:string;
 status:string;registered:boolean;assigned:boolean;syncStatus:string;
};

const listed=COMPANY_BRANDS;

export async function listWorkspaceApps(workspaceId:string):Promise<AppConnection[]>{
 const m=await getPrimaryMembership();
 if(m.workspace_id!==workspaceId)throw new Error('AgentOrdinary workspace mismatch.');
 const {data,error}=await supabase.from('agentopia_apps')
  .select('id,app_key,name,kind,status')
  .eq('workspace_id',workspaceId)
  .order('name').limit(100);
 if(error)throw error;
 const rows=data||[];
 const map=new Map(rows.map(x=>[String(x.app_key).toLowerCase(),x]));
 const known:AppConnection[]=listed.map(spec=>{
  const x=map.get(spec.key);
  return {key:spec.key,name:spec.name,kind:String(x?.kind||'Planned application'),
   status:String(x?.status||'not_registered'),registered:Boolean(x),assigned:false,syncStatus:'not_assigned'};
 });
 const named=new Set(known.map(x=>x.key));
 for(const x of rows){
  const key=String(x.app_key||'').toLowerCase();
  if(!named.has(key))known.push({key,name:key==='agentopia_web'?'AgentOrdinary':String(x.name),
   kind:String(x.kind||'Application'),status:String(x.status||'unknown'),registered:true,assigned:false,syncStatus:'not_assigned'});
 }
 return known;
}

export async function listEmployeeApps(workspaceId:string,agentId:string):Promise<AppConnection[]>{
 const m=await getPrimaryMembership();
 if(m.workspace_id!==workspaceId)throw new Error('AgentOrdinary workspace mismatch.');
 const [catalog,access]=await Promise.all([
  supabase.from('agentopia_apps')
   .select('id,app_key,name,kind,status').eq('workspace_id',workspaceId).limit(100),
  supabase.from('agentopia_agent_app_access')
   .select('app_id,enabled,sync_status')
   .eq('workspace_id',workspaceId).eq('agent_id',agentId).limit(100),
 ]);
 if(catalog.error)throw catalog.error;
 if(access.error)throw access.error;
 const registered=catalog.data||[];
 const byKey=new Map(registered.map(row=>[String(row.app_key).toLowerCase(),row]));
 const linked=new Map((access.data||[]).map(row=>[String(row.app_id),row]));
 const result:AppConnection[]=listed.map(spec=>{
  const row=byKey.get(spec.key);
  const assignment=row?linked.get(String(row.id)):undefined;
  const assigned=Boolean(assignment?.enabled);
  return {
   key:spec.key,name:spec.name,kind:String(row?.kind||'Planned application'),
   status:String(row?.status||'not_registered'),registered:Boolean(row),
   assigned,syncStatus:assigned?String(assignment?.sync_status||'unverified'):'not_assigned'
  };
 });
 const known=new Set(result.map(a=>a.key));
 for(const row of registered){
  const key=String(row.app_key||'').toLowerCase();
  if(known.has(key))continue;
  const a=linked.get(String(row.id));
  result.push({
   key,name:key==='agentopia_web'?'AgentOrdinary':String(row.name||key),
   kind:String(row.kind||'Application'),status:String(row.status||'unknown'),
   registered:true,assigned:Boolean(a?.enabled),
   syncStatus:a?.enabled?String(a.sync_status||'unverified'):'not_assigned'
  });
 }
 return result;
}


export type CatalogProvider={id:string;name:string;status:string;enabled:boolean;kind:'tool'|'model';freeOnly:boolean};
export async function listWorkspaceProviders(workspaceId:string):Promise<CatalogProvider[]>{
 const m=await getPrimaryMembership();
 if(m.workspace_id!==workspaceId)throw new Error('AgentOrdinary workspace mismatch.');
 const [toolRes,modelRes]=await Promise.all([
  supabase.from('agentopia_tool_providers')
   .select('id,display_name,health_status,enabled,free_only')
   .eq('workspace_id',workspaceId).order('display_name').limit(100),
  supabase.from('agentopia_model_providers')
   .select('id,display_name,health_status,enabled,free_only')
   .eq('workspace_id',workspaceId).order('display_name').limit(100),
 ]);
 if(toolRes.error)throw toolRes.error;
 if(modelRes.error)throw modelRes.error;
 return [
  ...(toolRes.data||[]).map(x=>({
   id:String(x.id),name:String(x.display_name),status:String(x.health_status||'unknown'),
   enabled:x.enabled===true,kind:'tool' as const,freeOnly:x.free_only===true
  })),
  ...(modelRes.data||[]).map(x=>({
   id:String(x.id),name:String(x.display_name),status:String(x.health_status||'unknown'),
   enabled:x.enabled===true,kind:'model' as const,freeOnly:x.free_only===true
  })),
 ];
}
